import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import moment from "moment";

import ApiService from "../../services/api-service";
import notifier from "../Notifier";
import { Images } from "../../assets/common";
import { truncate } from "../../constants/function";
import JukeboxSettings from "./JukeboxSettings";

/**
 * Pending Paid Requests panel for a host's own view (Studio / own ArtistProfile).
 * Shows audience-submitted paid song requests targeting this host —
 * including pre-stream ones that arrived before they went live.
 * Sorted by tipAmount desc (matches OBS Controls dock behavior).
 */
export default function HostPendingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pauseSaving, setPauseSaving] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.SONG_REQUEST_HOST_PENDING();
      setRequests(res?.result || []);
    } catch (e) {
      notifier.error("Could not load pending paid requests");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await ApiService.SONG_REQUEST_GET_CONFIG();
      if (res?.success) setPaused(Boolean(res.result?.submissionsPaused));
    } catch (e) {
      // Pause state is non-critical; fail quietly.
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchConfig();
  }, [fetchRequests, fetchConfig]);

  const handleTogglePause = async (event) => {
    const next = event.target.checked;
    setPaused(next); // optimistic
    setPauseSaving(true);
    try {
      await ApiService.SONG_REQUEST_UPDATE_CONFIG({ submissionsPaused: next });
      notifier.success(next ? "Requests paused" : "Requests resumed");
    } catch (e) {
      setPaused(!next); // revert on error
      notifier.error(e?.message || "Could not update request settings");
    } finally {
      setPauseSaving(false);
    }
  };

  const handleAccept = async (req) => {
    if (!req.roomId) {
      notifier.error("This request is waiting for you to go live before it can be accepted.");
      return;
    }
    setActingId(req._id);
    try {
      await ApiService.SONG_REQUEST_ACCEPT(req._id);
      notifier.success("Request accepted");
      await fetchRequests();
    } catch (e) {
      notifier.error(e?.message || "Failed to accept request");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (req) => {
    setActingId(req._id);
    try {
      await ApiService.SONG_REQUEST_REJECT(req._id);
      notifier.success("Request rejected and refunded");
      await fetchRequests();
    } catch (e) {
      notifier.error(e?.message || "Failed to reject request");
    } finally {
      setActingId(null);
    }
  };

  const jukeboxGear = (
    <Tooltip title="Jukebox Settings">
      <IconButton
        size="small"
        onClick={() => setSettingsOpen(true)}
        aria-label="Jukebox Settings"
        sx={{
          color: "#FFEE20",
          filter: "drop-shadow(0 0 6px rgba(255, 238, 32,0.45))",
          transition: "color 0.2s ease, filter 0.2s ease, transform 0.2s ease",
          "&:hover": {
            color: "#F5DC6A",
            filter: "drop-shadow(0 0 10px rgba(255, 238, 32,0.7))",
            transform: "rotate(45deg)",
            background: "rgba(255, 238, 32,0.08)",
          },
        }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const settingsDialog = (
    <JukeboxSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
  );

  const pauseToggle = (
    <Box>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            size="small"
            checked={paused}
            onChange={handleTogglePause}
            disabled={pauseSaving}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#FFEE20" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#FFEE20",
              },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{ fontFamily: "var(--font-body)", color: "rgba(255,255,255,0.85)" }}
          >
            Pause new requests
          </Typography>
        }
      />
      {paused && (
        <Typography
          variant="caption"
          display="block"
          sx={{ fontFamily: "var(--font-body)", color: "rgba(255, 238, 32,0.85)", mt: 0.25 }}
        >
          Fans can't submit right now.
        </Typography>
      )}
    </Box>
  );

  if (loading && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" gap={1} py={3}>
        <CircularProgress size={24} />
        {jukeboxGear}
        {settingsDialog}
      </Box>
    );
  }

  // No pending requests: still expose the Jukebox Settings entry point to the host.
  if (!loading && requests.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          mt: 2,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Jukebox
          </Typography>
          {jukeboxGear}
        </Stack>
        <Box mt={1.5}>{pauseToggle}</Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          No requests in the lineup yet.
        </Typography>
        {settingsDialog}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        mt: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Lineup
          </Typography>
          <Chip size="small" label={requests.length} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {jukeboxGear}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchRequests} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box mb={1.5}>{pauseToggle}</Box>

      <Stack spacing={1}>
        {requests.map((req, index) => {
          const isPreStream = !req.roomId;
          const requester = req.requesterId || {};
          const requesterName = requester.firstName
            ? `${requester.firstName} ${requester.lastName || ""}`.trim()
            : "Listener";
          return (
            <Box
              key={req._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <Typography
                sx={{
                  flexShrink: 0,
                  width: 24,
                  textAlign: "center",
                  fontFamily: "var(--font-display)",
                  fontSize: 15,
                  color: "rgba(255, 238, 32,0.85)",
                }}
              >
                #{index + 1}
              </Typography>
              <img
                src={req.songDetails?.thumbnailImg?.filePath || Images.default_music}
                alt={req.songDetails?.title}
                style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover" }}
              />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {truncate(req.songDetails?.title || "Untitled", 48)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {req.songDetails?.artist}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
                  <Avatar src={requester.profileImg?.filePath} sx={{ width: 16, height: 16 }} />
                  <Typography variant="caption" color="text.secondary">
                    {requesterName} · {moment(req.createdAt).fromNow()}
                  </Typography>
                  {isPreStream && (
                    <Chip size="small" label="Pre-stream" color="info" sx={{ height: 18, fontSize: 10 }} />
                  )}
                </Stack>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 70 }}>
                <RequestTierBadge tier={req.tier} tipAmount={req.tipAmount} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#FFEE20" }}>
                  {req.tipAmount} SIKI
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={isPreStream ? "Start streaming first" : "Accept"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAccept(req)}
                        disabled={isPreStream || actingId === req._id}
                        sx={{ color: "#4CAF50" }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reject and refund">
                    <IconButton
                      size="small"
                      onClick={() => handleReject(req)}
                      disabled={actingId === req._id}
                      sx={{ color: "#f44336" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      {settingsDialog}
    </Box>
  );
}

// Small per-row tier badge (FOL / Skip / Free / custom Offer), matching the
// requester-facing badge labels in SongRequestButton.
function RequestTierBadge({ tier, tipAmount }) {
  const labels = { fol: "FOL", skip: "Skip", free: "Free", custom: "Offer" };
  const label = labels[tier] || (Number(tipAmount) > 0 ? "Offer" : "Free");
  const isFree = tier === "free" || (!tier && !(Number(tipAmount) > 0));
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: "var(--font-body)",
        fontWeight: 700,
        fontSize: 9,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: isFree ? "rgba(255,255,255,0.55)" : "#FFEE20",
      }}
    >
      {label}
    </Typography>
  );
}
